import { levelsFor, NO_OVERRIDES, type Access, type AccessTable, type Level, type MyAccess } from './levels';

/**
 * The Discord bot's rows in the owner's one features × tiers table ("Who gets what", access.json) — owner's decision 12
 * (2026-11-01): "I need an owners section where I can decide which features are available to which tiers … This should
 * effect the bot too."
 *
 * Every slash command is either here (gated by the table) or in BOT_FREE_COMMANDS (never gated: linking, your key,
 * "Delete my data", help and the server's own setup — so nobody is ever locked out of those). The bot's tests check that
 * every command it has is one or the other.
 *
 *  - `checks: 'person'` — whoever uses the command: their own tier (a subscription, the owner's grant, or their faction's
 *    tier while they're in it). The bot knows who they are only once their Discord is tied to their Torn account by Torn
 *    itself or by their own key (`/account key` — in a DM with the bot too); then their tier counts in every server with
 *    the bot. Unknown: Basic.
 *  - `checks: 'faction'` — a part that runs for a faction by itself (its alerts): the faction's own tier.
 *
 * Default: every tier (as before the table existed); the owner unticks tiers to close a command. The bot never says which
 * tier anyone has — only "this needs a higher Omni-Torn tier" (decision 9: levels are for the owner's eyes only).
 */
export interface BotFeature {
  /** Feature id in the access table. */
  id: `bot-${string}`;
  /** What the owner sees in the table. */
  name: string;
  /** The bot part it belongs to (as the control panel names them). */
  group: 'Members' | 'Faction' | 'Giveaways' | 'Banking' | 'Stakeouts' | 'Server admin' | 'Runs by itself';
  /** The slash command it gates; none: a part that runs by itself. */
  command?: string;
  checks: 'person' | 'faction';
  /** Default tiers (none: every tier). */
  access?: Access;
  /** The buttons and forms (custom ids) that belong to the command — checked like the command itself. */
  buttons?: RegExp;
}

export const BOT_FEATURES: readonly BotFeature[] = [
  { id: 'bot-whois', name: '/whois — a member’s linked Torn profile', group: 'Members', command: 'whois', checks: 'person' },
  { id: 'bot-war', name: '/war — the faction’s ranked war, enemy travel, safe targets', group: 'Faction', command: 'war', checks: 'person' },
  { id: 'bot-oc', name: '/oc — organised crimes (who’s missing)', group: 'Faction', command: 'oc', checks: 'person' },
  { id: 'bot-members', name: '/members — the faction’s members right now', group: 'Faction', command: 'members', checks: 'person' },
  { id: 'bot-giveaway', name: '/giveaway — run and follow giveaways (entering by button is always free)', group: 'Giveaways', command: 'giveaway', checks: 'person', buttons: /^gw:(proof|proofsubmit|approve|done)(:|$)/ },
  { id: 'bot-balance', name: '/balance — your faction vault balance', group: 'Banking', command: 'balance', checks: 'person' },
  { id: 'bot-withdraw', name: '/withdraw — ask the bankers for money or points', group: 'Banking', command: 'withdraw', checks: 'person', buttons: /^bank:cancel(:|$)/ },
  { id: 'bot-stakeout', name: '/stakeout — watch a player or a faction', group: 'Stakeouts', command: 'stakeout', checks: 'person' },
  { id: 'bot-alerts', name: '/alerts — set up faction alerts (admins)', group: 'Server admin', command: 'alerts', checks: 'person' },
  { id: 'bot-armory', name: '/armory — items to keep stocked (admins)', group: 'Server admin', command: 'armory', checks: 'person' },
  { id: 'bot-bank', name: '/bank — banking setup (bankers, admins)', group: 'Server admin', command: 'bank', checks: 'person', buttons: /^bank:sent(:|$)/ },
  { id: 'bot-verifyall', name: '/verifyall — check every member’s link, nickname and roles (admins)', group: 'Server admin', command: 'verifyall', checks: 'person' },
  { id: 'bot-part-faction-alerts', name: 'Faction alerts posting (war pushes, retals, chain, OC…) — the faction’s tier', group: 'Runs by itself', checks: 'faction' },
];

/** Never gated by the table: linking, your key and data, help, and a server's own setup (`/omni`). */
export const BOT_FREE_COMMANDS: readonly string[] = ['account', 'link', 'unlink', 'verify', 'help', 'omni'];

/** The table row a button or form belongs to (null: free — entering a giveaway, your key, setup…). */
export function botFeatureForCustomId(customId: string): BotFeature | null {
  return BOT_FEATURES.find((f) => f.buttons?.test(customId)) ?? null;
}

/** The table row for a slash command (null: free, or unknown). */
export function botFeatureFor(command: string): BotFeature | null {
  return BOT_FEATURES.find((f) => f.command === command) ?? null;
}

export const FACTION_ALERTS_FEATURE = 'bot-part-faction-alerts';

/**
 * May this tier use this bot feature? The owner always; one player's own set (Owner → This player) next — "No" never,
 * "Yes" always; otherwise the table's row, else the feature's default (every tier). Unlike the extension and website
 * this doesn't need "Omni-Torn itself": the bot's servers are approved on their own (Owner → Discord servers), and its
 * members used the bot before tiers existed — the owner closes a command by unticking tiers on its row.
 */
export function canUseBotFeature(level: Level, feature: Pick<BotFeature, 'id' | 'access'>, table: AccessTable | null | undefined = {}, mine: MyAccess = NO_OVERRIDES): boolean {
  if (level === 'owner') return true;
  if (mine.deny.includes(feature.id)) return false;
  if (mine.allow.includes(feature.id)) return true;
  return (table?.[feature.id] ?? levelsFor(feature.access)).includes(level);
}

/** What the bot says when a tier doesn't reach — never which tier anyone has. */
export const TIER_NEEDED_TEXT = 'This needs a higher Omni-Torn tier.';
